/** 纯 SVG 雷达图：无额外依赖，可直接进入教师复盘/评分卡。 */
import { useId } from 'react';

export interface RadarSeries {
  label: string;
  color: string;
  values: number[];
}

export function RadarChart({
  labels,
  series,
  max = 5,
  size = 220,
}: {
  labels: string[];
  series: RadarSeries[];
  max?: number;
  size?: number;
}) {
  const id = useId().replace(/[:]/g, '');
  const center = size / 2;
  const radius = size * 0.34;
  const count = Math.max(3, labels.length);

  const point = (index: number, value: number): [number, number] => {
    const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
    const r = (Math.max(0, Math.min(max, value)) / max) * radius;
    return [center + Math.cos(angle) * r, center + Math.sin(angle) * r];
  };

  const ring = (ratio: number) =>
    Array.from({ length: count }, (_, index) => {
      const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
      const r = radius * ratio;
      return `${center + Math.cos(angle) * r},${center + Math.sin(angle) * r}`;
    }).join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="radar-chart" role="img" aria-label="评分雷达图">
      <defs>
        {series.map((item, index) => (
          <radialGradient key={index} id={`${id}-fill-${index}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor={item.color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={item.color} stopOpacity="0.06" />
          </radialGradient>
        ))}
      </defs>
      {[0.25, 0.5, 0.75, 1].map((ratio) => (
        <polygon
          key={ratio}
          points={ring(ratio)}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="1"
        />
      ))}
      {Array.from({ length: count }, (_, index) => {
        const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
        const x = center + Math.cos(angle) * radius;
        const y = center + Math.sin(angle) * radius;
        const lx = center + Math.cos(angle) * (radius + 18);
        const ly = center + Math.sin(angle) * (radius + 18);
        return (
          <g key={index}>
            <line x1={center} y1={center} x2={x} y2={y} stroke="rgba(255,255,255,0.08)" />
            <text
              x={lx}
              y={ly}
              textAnchor={lx < center - 6 ? 'end' : lx > center + 6 ? 'start' : 'middle'}
              dominantBaseline="middle"
              fontSize="8.5"
              fill="#94a3b8"
            >
              {labels[index] ?? ''}
            </text>
          </g>
        );
      })}
      {series.map((item, index) => (
        <g key={item.label}>
          <polygon
            points={item.values
              .slice(0, count)
              .map((value, valueIndex) => point(valueIndex, value).join(','))
              .join(' ')}
            fill={`url(#${id}-fill-${index})`}
            stroke={item.color}
            strokeWidth="1.4"
          />
          {item.values.slice(0, count).map((value, valueIndex) => {
            const [x, y] = point(valueIndex, value);
            return <circle key={valueIndex} cx={x} cy={y} r="1.8" fill={item.color} />;
          })}
        </g>
      ))}
    </svg>
  );
}
